import { StatusBar } from "@-/com.npmjs.expo-status-bar";
import {
  BackHandler,
  type LayoutChangeEvent,
  Pressable,
  ScrollView,
  useWindowDimensions,
  View,
} from "@-/com.npmjs.react-native";
import Animated, {
  cancelAnimation,
  Easing,
  FadeIn,
  FadeOut,
  runOnJS,
  runOnUI,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import {
  fillOnboardingSummaryPlaceholders,
  ONBOARDING_INTRO_PAGES,
  type OnboardingIntroPage,
  type OnboardingIntroQuestionPage,
  type OnboardingSummaryItem,
  type OnboardingSummaryPage,
  type OnboardingSummaryPlaceholder,
  type OnboardingWelcomePage,
} from "@-/dev.improve.haniverse.assets.onboarding";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import {
  completeOnboardingSurvey,
  estimateCharactersInThreeMonths,
  playOnboardingReaction,
  recordOnboardingAnswers,
  useOnboardingState,
} from "@-/dev.improve.haniverse.lib.onboarding";
import type { OnboardingIntroOverlayGate } from "#use-onboarding-intro-overlay-gate";
import { useTheme } from "@-/dev.improve.haniverse.lib.theme";
import { useAsyncComponent } from "@-/dev.improve.haniverse.lib.use-async-component";
import { welcomeGalaxyPalette } from "@-/dev.improve.haniverse.routes.nav.app.auth/welcome-galaxy-palette";
import { publishWelcomeGalaxyDiveTarget } from "@-/dev.improve.haniverse.routes.nav.app.auth.welcome-galaxy-entry";
import {
  onboardingMapFrame,
  type OnboardingMapFrame,
} from "@-/dev.improve.haniverse.routes.nav.app.onboarding.handoff";
import {
  createElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { loadOnboardingGlobeCanvas } from "#load-onboarding-globe-canvas";
import { getOnboardingGlobeGeometry } from "#onboarding-globe-geometry";
import type { OnboardingGlobeMotion } from "#onboarding-globe-motion";
import { onboardingGlobePalette } from "#onboarding-globe-palette";
import { onboardingTransitionCompletion } from "#onboarding-transition-completion";
import { OnboardingQuestionInputGate } from "#onboarding-question-input-gate";
import { OnboardingGuideMessage } from "#onboarding-speech-bubble";
import {
  OnboardingBackButton,
  OnboardingChoice,
  OnboardingContinueButton,
  OnboardingEmoji,
  OnboardingProgressBar,
} from "#onboarding-intro-controls";
import {
  getFallbackOnboardingMapFrame,
  getOnboardingGlobeLayout,
  ONBOARDING_GLOBE_LANDING_DURATION_MS,
  ONBOARDING_HEADER_INSET,
  type OnboardingGlobeLayout,
  type OnboardingGlobeViewport,
  useOnboardingGlobe,
} from "#use-onboarding-globe";

/**
 * How long the Earth waits for its canvas before the onboarding starts anyway: the pages then show
 * over the plain sky, as in a build without Skia.
 */
const CANVAS_LOAD_DEADLINE_MS = 1500;
/**
 * When the globe floats up to make room for the welcome (after its arrival has started), once it
 * has settled facing China.
 */
const RAISE_DELAY_MS = 3000;
const WELCOME_TEXT_DELAY_MS = RAISE_DELAY_MS + 350;
/** The overlay's fade off the map once China has become it. */
const EXIT_FADE_MS = 550;
/** Slack past the landing for the frame clock, which stops in the background. */
const LANDING_DEADLINE_SLACK_MS = 1500;

/** The globe is raised for the welcome, and waits in the corner while the learner answers. */
function getGlobeLayout(page: OnboardingIntroPage): OnboardingGlobeLayout {
  return page.kind === "welcome" ? "raised" : "corner";
}

/**
 * The start of a new learner's onboarding, over the whole app (the Learn map is already open
 * beneath it): the Earth comes in from deep space and turns to face China, floats up for
 * "Welcome to the Haniverse." and what the Haniverse is, then waits in the top-right corner while
 * the learner answers a few questions, Duolingo style (every Continue there spins it round once).
 * Get Started brings it back and in close, until China becomes the Learn map's own outline and the
 * rest of the world falls away; the overlay then fades off the map and the onboarding moves on to
 * the map's tour (`OnboardingSheet`). Its pages are authored in `assets/onboarding`'s `intro`
 * files, the welcome first and the summary last.
 */
export function OnboardingIntroOverlay({
  gate: _gate,
}: {
  gate: OnboardingIntroOverlayGate;
}) {
  const [hasTransitionFailed, setHasTransitionFailed] = useState(false);
  const onTransitionFailed = useCallback(() => setHasTransitionFailed(true), []);
  const retry = useCallback(() => setHasTransitionFailed(false), []);
  const insets = useSafeAreaInsets();
  useEffect(() => {
    if (!hasTransitionFailed) {
      return;
    }
    const subscription = BackHandler.addEventListener("hardwareBackPress", () => {
      retry();
      return true;
    });
    return () => subscription.remove();
  }, [hasTransitionFailed, retry]);

  // Unmount the entire animation session on failure. A static recovery surface cannot overlap
  // a stranded globe, and retry starts fresh without losing the learner's recorded answers.
  if (hasTransitionFailed) {
    return (
      <View
        accessibilityViewIsModal
        className="absolute inset-0 z-50 justify-center gap-6 bg-background px-5"
        style={{ paddingBottom: insets.bottom + 12, paddingTop: insets.top + 12 }}
        testID="onboarding-transition-recovery"
      >
        <Text accessibilityRole="header" className="text-center text-2xl font-bold text-foreground">
          Let’s try that again
        </Text>
        <Text className="text-center text-base text-muted-foreground">Your answers are saved.</Text>
        <OnboardingContinueButton
          label="Try again"
          onPress={retry}
          testID="onboarding-transition-retry-button"
        />
      </View>
    );
  }
  return <OnboardingIntroSession onTransitionFailed={onTransitionFailed} />;
}

function OnboardingIntroSession({
  onTransitionFailed,
}: {
  onTransitionFailed: () => void;
}) {
  const appearance = useTheme();
  const palette = onboardingGlobePalette(appearance);
  const reduceMotion = useReducedMotion();
  const insets = useSafeAreaInsets();
  const { height, width } = useWindowDimensions();
  const { diameter } = useToolbarGridMetrics();
  const viewport = useMemo<OnboardingGlobeViewport>(
    () => ({ headerDiameter: diameter, height, topInset: insets.top, width }),
    [diameter, height, insets.top, width],
  );
  const { arrive, land, motion, moveTo, press, spin } = useOnboardingGlobe({
    onTransitionFailed,
    reduceMotion,
    viewport,
  });
  const Canvas = useAsyncComponent(loadOnboardingGlobeCanvas);
  const [page, setPage] = useState<OnboardingIntroPage>(ONBOARDING_INTRO_PAGES[0]);
  const pageRef = useRef<OnboardingIntroPage>(ONBOARDING_INTRO_PAGES[0]);
  const [isGlobeParked, setIsGlobeParked] = useState(false);
  const globeParked = useCallback(() => setIsGlobeParked(true), []);
  const [isWelcomeShown, setIsWelcomeShown] = useState(reduceMotion);
  const [isLanding, setIsLanding] = useState(false);
  const overlayOpacity = useSharedValue(1);
  const originRef = useRef({ x: 0, y: 0 });
  const rootRef = useRef<View>(null);
  const mapFrame = useSharedValue<OnboardingMapFrame | null>(null);
  const [isCanvasOverdue, setIsCanvasOverdue] = useState(false);
  const isStarted = Canvas !== null || isCanvasOverdue;

  useEffect(() => {
    if (Canvas !== null) {
      return;
    }
    const deadline = setTimeout(setIsCanvasOverdue, CANVAS_LOAD_DEADLINE_MS, true);
    return () => clearTimeout(deadline);
  }, [Canvas]);

  // The Earth is on screen beneath the welcome galaxy's dive into its core (when a new learner
  // came that way), which then dissolves into it while it is still diving
  // (`WelcomeGalaxyEntryHost`).
  useEffect(() => {
    if (Canvas === null) {
      return;
    }
    publishWelcomeGalaxyDiveTarget(true);
    return () => publishWelcomeGalaxyDiveTarget(false);
  }, [Canvas]);

  // Arrive out of the galaxy's core as soon as the Earth can draw, then float up and say welcome.
  useEffect(() => {
    if (pageRef.current.kind !== "welcome") {
      moveTo("corner", globeParked);
      return;
    }
    if (reduceMotion) {
      moveTo("raised");
      return;
    }
    if (!isStarted) {
      return;
    }
    arrive();
    const raise = setTimeout(moveTo, RAISE_DELAY_MS, "raised");
    const welcome = setTimeout(setIsWelcomeShown, WELCOME_TEXT_DELAY_MS, true);
    return () => {
      clearTimeout(raise);
      clearTimeout(welcome);
    };
  }, [arrive, globeParked, isStarted, moveTo, reduceMotion]);

  const goTo = useCallback(
    (next: OnboardingIntroPage) => {
      if (next.kind === "welcome" || pageRef.current.kind === "welcome") {
        setIsGlobeParked(false);
      }
      pageRef.current = next;
      setPage(next);
      moveTo(getGlobeLayout(next), next.kind === "welcome" ? undefined : globeParked);
    },
    [globeParked, moveTo],
  );
  const pageIndex = ONBOARDING_INTRO_PAGES.indexOf(page);
  const goBack = useCallback(() => {
    const previous = ONBOARDING_INTRO_PAGES[pageIndex - 1];
    if (previous !== undefined && !isLanding) {
      goTo(previous);
    }
  }, [goTo, isLanding, pageIndex]);
  const goNext = useCallback(() => {
    const next = ONBOARDING_INTRO_PAGES[pageIndex + 1];
    if (next !== undefined) {
      goTo(next);
      // Every step through the questions turns the Earth round once in its corner, while the
      // progress bar fills. Leaving the welcome only moves it up there, without a turn.
      if (getGlobeLayout(page) === "corner") {
        spin();
      }
    }
  }, [goTo, page, pageIndex, spin]);

  // Android's back steps back through the pages, and never leaves the onboarding for the app.
  useEffect(() => {
    const subscription = BackHandler.addEventListener("hardwareBackPress", () => {
      goBack();
      return true;
    });
    return () => subscription.remove();
  }, [goBack]);

  const finish = useCallback(() => {
    playOnboardingReaction("intro");
  }, []);

  const getStarted = useCallback(() => {
    completeOnboardingSurvey();
    setIsLanding(true);
    const reported = onboardingMapFrame().get();
    const geometry = getOnboardingGlobeGeometry();
    // The map reports window coordinates; the overlay may not start at the window's corner.
    const frame =
      reported === null
        ? getFallbackOnboardingMapFrame(viewport, geometry.map)
        : {
            scale: reported.scale,
            x: reported.x - originRef.current.x,
            y: reported.y - originRef.current.y,
          };
    mapFrame.set(frame);
    land(frame, () => {
      overlayOpacity.set(withTiming(0, { duration: reduceMotion ? 0 : EXIT_FADE_MS }));
      setTimeout(finish, reduceMotion ? 0 : EXIT_FADE_MS);
    });
  }, [finish, land, mapFrame, overlayOpacity, reduceMotion, viewport]);

  // The landing must always end in the map, even if its animation never reports back.
  useEffect(() => {
    if (!isLanding) {
      return;
    }
    const deadline = setTimeout(
      finish,
      ONBOARDING_GLOBE_LANDING_DURATION_MS + EXIT_FADE_MS + LANDING_DEADLINE_SLACK_MS,
    );
    return () => clearTimeout(deadline);
  }, [finish, isLanding]);

  const overlayStyle = useAnimatedStyle(() => ({ opacity: overlayOpacity.get() }));
  const onLayout = useCallback((_event: LayoutChangeEvent) => {
    rootRef.current?.measureInWindow((x, y) => {
      originRef.current = { x, y };
    });
  }, []);

  return (
    <Animated.View
      accessibilityViewIsModal
      className="absolute inset-0 z-50"
      onLayout={onLayout}
      ref={rootRef}
      style={overlayStyle}
      testID="onboarding-intro-overlay"
    >
      <StatusBar style={palette.statusBarStyle} />
      <View className={`absolute inset-0 ${welcomeGalaxyPalette(appearance).pageClassName}`} />
      <View className="absolute inset-0" pointerEvents="none">
        {Canvas === null
          ? null
          : createElement(Canvas, {
              appearance,
              mapFrame,
              motion,
              viewport: { height, width },
            })}
      </View>
      {isLanding ? null : (
        <>
          <OnboardingIntroStep
            goBack={goBack}
            goNext={goNext}
            isGlobeParked={isGlobeParked}
            isWelcomeShown={isWelcomeShown}
            onGetStarted={getStarted}
            onTransitionFailed={onTransitionFailed}
            page={page}
            viewport={viewport}
          />
          <OnboardingGlobePressTarget motion={motion} onPress={press} />
        </>
      )}
    </Animated.View>
  );
}

export function OnboardingIntroStep({
  goBack,
  goNext,
  isGlobeParked,
  isWelcomeShown,
  onGetStarted,
  onTransitionFailed,
  page,
  viewport,
}: {
  goBack: () => void;
  goNext: () => void;
  isGlobeParked: boolean;
  isWelcomeShown: boolean;
  onGetStarted: () => void;
  onTransitionFailed: () => void;
  page: OnboardingIntroPage;
  viewport: OnboardingGlobeViewport;
}) {
  const insets = useSafeAreaInsets();
  if (page.kind === "welcome") {
    const raised = getOnboardingGlobeLayout("raised", viewport);
    // The welcome waits for the Earth to arrive and float up; until then the page is the globe.
    return (
      <View className="flex-1" style={{ paddingBottom: insets.bottom + 12 }}>
        <ScrollView
          className="flex-1"
          contentContainerClassName="px-4 pb-4"
          contentContainerStyle={{ paddingTop: raised.centerY + raised.radius + 24 }}
          showsVerticalScrollIndicator={false}
        >
          {isWelcomeShown ? (
            <Animated.View
              className="w-full"
              entering={FadeIn.duration(600)}
              exiting={FadeOut.duration(250)}
            >
              <OnboardingGuideMessage testID="onboarding-welcome-speech-bubble">
                <OnboardingWelcomeCopy page={page} />
              </OnboardingGuideMessage>
            </Animated.View>
          ) : null}
        </ScrollView>
        {isWelcomeShown ? (
          <Animated.View className="px-4" entering={FadeIn.duration(600).delay(600)}>
            <OnboardingContinueButton
              icon={page.icon}
              label={page.button}
              onPress={goNext}
              testID="onboarding-welcome-continue-button"
            />
          </Animated.View>
        ) : null}
      </View>
    );
  }
  return (
    <OnboardingQuestionPage
      goBack={goBack}
      goNext={goNext}
      isGlobeParked={isGlobeParked}
      onGetStarted={onGetStarted}
      onTransitionFailed={onTransitionFailed}
      page={page}
      viewport={viewport}
    />
  );
}

/** The press target's own radius (`size-25` across), before its transform scales it to the globe's. */
const PRESS_TARGET_RADIUS = 50;

/**
 * Where the globe is, as something to press: it bounces under the finger like a glass button
 * (`useOnboardingGlobe`'s `press`), and does nothing else, so it is no button to a screen reader.
 */
function OnboardingGlobePressTarget({
  motion,
  onPress,
}: {
  motion: OnboardingGlobeMotion;
  onPress: (isPressed: boolean) => void;
}) {
  const { centerX, centerY, radius } = motion;
  // A circle of a fixed size that a transform places and sizes: moving it never lays it out again.
  const style = useAnimatedStyle(() => ({
    transform: [
      { translateX: centerX.get() - PRESS_TARGET_RADIUS },
      { translateY: centerY.get() - PRESS_TARGET_RADIUS },
      { scale: radius.get() / PRESS_TARGET_RADIUS },
    ],
  }));
  return (
    <Animated.View className="absolute top-0 left-0 size-25 rounded-full" style={style}>
      <Pressable
        accessible={false}
        className="size-full rounded-full"
        onPressIn={() => onPress(true)}
        onPressOut={() => onPress(false)}
        testID="onboarding-globe"
      />
    </Animated.View>
  );
}

/** The welcome, and beneath it what the Haniverse is. */
function OnboardingWelcomeCopy({ page }: { page: OnboardingWelcomePage }) {
  return (
    <>
      <Text accessibilityRole="header" className="text-2xl leading-tight font-bold text-foreground">
        {page.title}
      </Text>
      <Animated.View className="gap-3" entering={FadeIn.duration(600).delay(300)}>
        {page.paragraphs.map((paragraph) => (
          <Text className="text-base leading-relaxed text-muted-foreground" key={paragraph}>
            {paragraph}
          </Text>
        ))}
      </Animated.View>
    </>
  );
}

const QUESTION_PAGE_FADE_IN_MS = 350;
const QUESTION_PAGE_OFFSET = 12;

/**
 * A question page's content, which fades in each time a page mounts (it is keyed by the page). The
 * fade is an animated style rather than an `entering` layout animation. On Android that animation
 * left a question page at opacity 0 when the learner came back to it: it reported finishing, but
 * the page's native view kept the opacity 0 it had been inserted with, so the page stayed blank
 * under its progress bar and Continue.
 */
function OnboardingQuestionFadeIn({
  children,
  onTransitionFailed,
}: {
  children: ReactNode;
  onTransitionFailed: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const [isReady, setIsReady] = useState(reduceMotion);
  const opacity = useSharedValue(reduceMotion ? 1 : 0);
  useEffect(() => {
    const transition = onboardingTransitionCompletion({
      onFailed: onTransitionFailed,
      onReady: () => setIsReady(true),
      parts: 1,
      reduceMotion,
      snapToEnd: (onSnapped) => {
        runOnUI(() => {
          cancelAnimation(opacity);
          opacity.set(1);
          runOnJS(onSnapped)();
        })();
      },
    });
    const { completePart } = transition;
    opacity.set(
      withTiming(
        1,
        {
          duration: reduceMotion ? 0 : QUESTION_PAGE_FADE_IN_MS,
          easing: Easing.out(Easing.cubic),
        },
        (finished) => runOnJS(completePart)(0, finished === true),
      ),
    );
    return () => {
      transition.cancel();
      cancelAnimation(opacity);
    };
  }, [onTransitionFailed, opacity, reduceMotion]);
  const style = useAnimatedStyle(() => ({
    opacity: opacity.get(),
    transform: [{ translateY: -QUESTION_PAGE_OFFSET * (1 - opacity.get()) }],
  }));
  return (
    <Animated.View className="flex-1" style={style}>
      <OnboardingQuestionInputGate isReady={isReady}>{children}</OnboardingQuestionInputGate>
    </Animated.View>
  );
}

/** The pages the progress bar counts: every page after the welcome, the summary included. */
const QUESTION_COUNT = ONBOARDING_INTRO_PAGES.length - 1;
/** The gap between the progress bar's end and the globe in the corner: the toolbar's 9pt gutter. */
const PROGRESS_BAR_GLOBE_GAP = ONBOARDING_HEADER_INSET;

function OnboardingQuestionPage({
  goBack,
  goNext,
  isGlobeParked,
  onGetStarted,
  onTransitionFailed,
  page,
  viewport,
}: {
  goBack: () => void;
  goNext: () => void;
  isGlobeParked: boolean;
  onGetStarted: () => void;
  onTransitionFailed: () => void;
  page: OnboardingIntroQuestionPage | OnboardingSummaryPage;
  viewport: OnboardingGlobeViewport;
}) {
  const reduceMotion = useReducedMotion();
  const backOpacity = useSharedValue(reduceMotion ? 1 : 0);
  const trackReveal = useSharedValue(reduceMotion ? 1 : 0);
  useEffect(() => {
    backOpacity.set(withTiming(1, { duration: reduceMotion ? 0 : 150 }));
    return () => cancelAnimation(backOpacity);
  }, [backOpacity, reduceMotion]);
  useEffect(() => {
    if (isGlobeParked) {
      trackReveal.set(
        withTiming(1, {
          duration: reduceMotion ? 0 : QUESTION_PAGE_FADE_IN_MS,
          easing: Easing.out(Easing.cubic),
        }),
      );
    }
    return () => cancelAnimation(trackReveal);
  }, [isGlobeParked, reduceMotion, trackReveal]);
  const backStyle = useAnimatedStyle(() => ({ opacity: backOpacity.get() }));
  const trackStyle = useAnimatedStyle(() => ({
    transform: [{ scaleX: trackReveal.get() }],
    transformOrigin: "left center",
  }));
  const corner = getOnboardingGlobeLayout("corner", viewport);
  const insets = useSafeAreaInsets();
  const state = useOnboardingState();
  // The welcome is always the first page (`_build-onboarding` checks).
  const questionIndex = ONBOARDING_INTRO_PAGES.indexOf(page) - 1;
  let question: string = page.title;
  let body: ReactNode;
  let action: ReactNode;
  switch (page.kind) {
    case "heard-about": {
      body = page.options.map((option) => (
        <OnboardingChoice
          key={option.id}
          label={option.label}
          onPress={() => recordOnboardingAnswers({ heardAbout: option.id })}
          selected={state?.heardAbout === option.id}
          testID={`onboarding-heard-about-${option.id}`}
        />
      ));
      action = (
        <OnboardingContinueButton
          disabled={state?.heardAbout == null}
          icon={page.icon}
          label={page.button}
          onPress={goNext}
          testID="onboarding-heard-about-continue-button"
        />
      );
      break;
    }
    case "reasons": {
      const reasons = state?.reasons ?? [];
      body = page.options.map((option) => {
        const selected = reasons.includes(option.id);
        return (
          <OnboardingChoice
            isMultiple
            key={option.id}
            label={option.label}
            leading={<OnboardingEmoji size={28}>{option.emoji}</OnboardingEmoji>}
            onPress={() =>
              recordOnboardingAnswers({
                reasons: selected
                  ? reasons.filter((reason) => reason !== option.id)
                  : [...reasons, option.id],
              })
            }
            selected={selected}
            testID={`onboarding-reason-${option.id}`}
          />
        );
      });
      action = (
        <OnboardingContinueButton
          disabled={reasons.length === 0}
          icon={page.icon}
          label={page.button}
          onPress={goNext}
          testID="onboarding-reasons-continue-button"
        />
      );
      break;
    }
    case "daily-goal": {
      body = page.options.map((option) => (
        <OnboardingChoice
          key={option.minutes}
          label={option.label}
          onPress={() => recordOnboardingAnswers({ dailyGoalMinutes: option.minutes })}
          selected={state?.dailyGoalMinutes === option.minutes}
          testID={`onboarding-daily-goal-${option.minutes}`}
          trailing={option.pace}
        />
      ));
      action = (
        <OnboardingContinueButton
          disabled={state?.dailyGoalMinutes == null}
          icon={page.icon}
          label={page.button}
          onPress={goNext}
          testID="onboarding-daily-goal-continue-button"
        />
      );
      break;
    }
    default: {
      const minutes = state?.dailyGoalMinutes ?? DEFAULT_DAILY_GOAL_MINUTES;
      const placeholders = { characters: estimateCharactersInThreeMonths(minutes), minutes };
      question = fillOnboardingSummaryPlaceholders(page.title, placeholders);
      body = <OnboardingSummary items={page.items} placeholders={placeholders} />;
      action = (
        <OnboardingContinueButton
          icon={page.icon}
          label={page.button}
          onPress={onGetStarted}
          testID="onboarding-get-started-button"
        />
      );
    }
  }
  return (
    <View className="flex-1" style={{ paddingTop: insets.top + ONBOARDING_HEADER_INSET }}>
      {/* The toolbar's row: back where the shell's back control sits, the globe (drawn by the
          canvas) in the top-right slot, and the progress between them, a gutter clear of the
          globe. */}
      <View
        className="flex-row items-center gap-3"
        style={{
          height: viewport.headerDiameter,
          paddingLeft: ONBOARDING_HEADER_INSET,
          paddingRight: ONBOARDING_HEADER_INSET + corner.radius * 2 + PROGRESS_BAR_GLOBE_GAP,
        }}
      >
        <Animated.View style={backStyle}>
          <OnboardingBackButton onPress={goBack} />
        </Animated.View>
        <Animated.View className="h-5.5 flex-1" style={trackStyle}>
          <OnboardingProgressBar progress={(questionIndex + 1) / QUESTION_COUNT} />
        </Animated.View>
      </View>
      {isGlobeParked ? (
        <OnboardingQuestionFadeIn key={page.kind} onTransitionFailed={onTransitionFailed}>
          <ScrollView
            className="mt-4 flex-1"
            contentContainerClassName="gap-3 px-4 pt-2 pb-6"
            showsVerticalScrollIndicator={false}
          >
            <View className="mb-3">
              <OnboardingGuideMessage testID="onboarding-question-speech-bubble">
                <Text
                  accessibilityRole="header"
                  className="text-2xl leading-snug font-bold text-foreground"
                >
                  {question}
                </Text>
              </OnboardingGuideMessage>
            </View>
            {body}
          </ScrollView>
          <View
            className="border-t border-border px-4 pt-3"
            style={{ paddingBottom: insets.bottom + 12 }}
          >
            {action}
          </View>
        </OnboardingQuestionFadeIn>
      ) : null}
    </View>
  );
}

/** The summary's goal, should the learner somehow reach it without having chosen one. */
const DEFAULT_DAILY_GOAL_MINUTES = 10;

/** What the learner can achieve, with `{characters}` and `{minutes}` filled in from their goal. */
function OnboardingSummary({
  items,
  placeholders,
}: {
  items: readonly OnboardingSummaryItem[];
  placeholders: Readonly<Record<OnboardingSummaryPlaceholder, number>>;
}) {
  return (
    <View className="gap-7 pt-2">
      {items.map((item) => (
        <View className="flex-row items-start gap-4 px-2" key={item.title}>
          <OnboardingEmoji size={34}>{item.emoji}</OnboardingEmoji>
          <View className="flex-1 gap-1">
            <Text className="text-xl font-bold text-foreground">
              {fillOnboardingSummaryPlaceholders(item.title, placeholders)}
            </Text>
            <Text className="text-base leading-snug text-muted-foreground">
              {fillOnboardingSummaryPlaceholders(item.subtitle, placeholders)}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
}
